import React, {
  forwardRef, useEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useForm, useController } from 'react-hook-form';
import { BsCheck } from 'react-icons/bs';

import Loader from '../Loader';
import ModalStandalone from '../../lib/ModalStandalone';
import { loadOptionsCase } from '../../utils';
import { getData } from '../../actions';
import confirmStyles from '../../lib/withModalConfirm/modal.module.scss';

import styles from './move-image-to-document-modal.module.scss';

const DEFAULT_MODE = 'move';

const normalizeNumber = (value) => (
  String(value || '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .trim()
);

const MoveImageToDocumentModal = forwardRef(({
  fileId,
  fileIds = null,
  pictureType,
  getSourceForFile,
  onSubmitTransfer,
}, ref) => {
  const modalRoot = useMemo(() => document.getElementById('modal'), []);

  const [mode, setMode] = useState(DEFAULT_MODE);
  const [isLoadingCases, setIsLoadingCases] = useState(false);
  const [isLoadingNumbers, setIsLoadingNumbers] = useState(false);

  const [caseOptions, setCaseOptions] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [numberOptions, setNumberOptions] = useState([]);
  const [numberSearch, setNumberSearch] = useState('');

  const [pendingConfirm, setPendingConfirm] = useState({ action: null, message: null });

  const { control, watch, reset } = useForm();
  const { field } = useController({
    name: 'search',
    control,
    defaultValue: '',
  });
  const watchSearch = watch('search');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoadingCases(true);
      const cases = await loadOptionsCase(watchSearch || '');
      if (cancelled) return;
      setCaseOptions(cases);
      setIsLoadingCases(false);
    };
    load();
    return () => { cancelled = true; };
  }, [watchSearch]);

  useEffect(() => {
    let cancelled = false;
    const loadNumbers = async () => {
      if (!selectedCaseId) return;
      setIsLoadingNumbers(true);
      try {
        const [tasksRes, sageRes] = await Promise.all([
          getData(null, `/task?caseId=${selectedCaseId}`, null, true),
          getData(null, `/sage/case/${selectedCaseId}/documents`, null, true),
        ]);

        const tasks = tasksRes?.data?.tasks || [];
        const sageDocs = sageRes?.data?.documents || [];

        const byNormalized = new Map();

        tasks.forEach((t) => {
          const n = t.number;
          if (!n) return;
          const key = normalizeNumber(n);
          if (byNormalized.has(key)) return;
          byNormalized.set(key, {
            key,
            label: `${n} (${t.type || ''})`,
            number: n,
            kind: 'task',
          });
        });

        const pushSageNumber = (n) => {
          if (!n) return;
          const key = normalizeNumber(n);
          if (byNormalized.has(key)) return;
          byNormalized.set(key, {
            key,
            label: n,
            number: n,
            kind: 'sage',
          });
        };

        sageDocs.forEach((d) => {
          // API returns { piece, piecePL, date, ... }
          pushSageNumber(d.piece);
          pushSageNumber(d.piecePL);
        });

        const opts = Array.from(byNormalized.values());
        opts.sort((a, b) => a.label.localeCompare(b.label));

        if (!cancelled) setNumberOptions(opts);
      } finally {
        if (!cancelled) setIsLoadingNumbers(false);
      }
    };
    loadNumbers();
    return () => { cancelled = true; };
  }, [selectedCaseId]);

  function closeModal() {
    if (ref?.current?.close) ref.current.close();
    setPendingConfirm({ action: null, message: null });
    setMode(DEFAULT_MODE);
    setSelectedCaseId(null);
    setSelectedNumber(null);
    setNumberOptions([]);
    setNumberSearch('');
    reset({ search: '' });
  }

  function confirmAction(bool) {
    if (bool && pendingConfirm?.action) pendingConfirm.action();
    setPendingConfirm({ action: null, message: null });
  }

  function requestTransfer() {
    const ids = Array.isArray(fileIds) && fileIds.length > 0 ? fileIds : [fileId].filter(Boolean);
    if (ids.length === 0 || !selectedCaseId || !selectedNumber) return;
    const message = mode === 'copy'
      ? `Copier ${ids.length > 1 ? `${ids.length} images` : 'l\'image'} vers le document ${selectedNumber} ?`
      : `Déplacer ${ids.length > 1 ? `${ids.length} images` : 'l\'image'} vers le document ${selectedNumber} ?`;

    setPendingConfirm({
      message,
      action: async () => {
        const promises = ids.map((id) => {
          const source = getSourceForFile(id);
          return onSubmitTransfer({
            fileId: id,
            targetCaseId: selectedCaseId,
            targetNumber: selectedNumber,
            pictureType,
            mode,
            source,
          });
        });
        await Promise.all(promises);
        closeModal();
      },
    });
  }

  return (
    <>
      {(pendingConfirm.action && pendingConfirm.message && modalRoot) && createPortal(
        <>
          <div className={confirmStyles['overlay-modal']} />
          <div className={confirmStyles.modal}>
            <p>{pendingConfirm.message}</p>
            <div className={confirmStyles['container-btn']}>
              <button onClick={() => confirmAction(true)}>
                <span>Confirmer</span>
              </button>
              <button onClick={() => confirmAction(false)}>
                <span>Annuler</span>
              </button>
            </div>
          </div>
        </>,
        modalRoot,
      )}

      <ModalStandalone ref={ref} handleClose={closeModal}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <h1>Déplacer vers un numéro de document</h1>

          {!selectedCaseId ? (
            <>
              <div className={styles.fieldSearch}>
                <input
                  type="search"
                  placeholder="Rechercher une affaire..."
                  name={field.name}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              </div>

              <div className={styles.list}>
                {isLoadingCases ? (
                  <div className={styles.loader}><Loader size={22} /></div>
                ) : (
                  <ul>
                    {(caseOptions || []).map((c) => (
                      <li
                        key={`case-${c.value}`}
                        onClick={() => setSelectedCaseId(c.value)}
                      >
                        <div className={`${styles.status} ${styles[c.status || 'pending']}`} />
                        <p>{c.label}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <>
              <div className={styles.subTitle}>
                <p>Choisir le numéro de document</p>
              </div>

              <div className={styles.fieldSearch}>
                <input
                  type="search"
                  placeholder="Rechercher un numéro (LPT, LL, LRT…)..."
                  value={numberSearch}
                  onChange={(e) => setNumberSearch(e.target.value)}
                />
              </div>

              <div className={styles.list}>
                {isLoadingNumbers ? (
                  <div className={styles.loader}><Loader size={22} /></div>
                ) : (
                  <ul>
                    {(numberOptions || []).filter((n) => {
                      if (!numberSearch) return true;
                      const query = normalizeNumber(numberSearch);
                      return normalizeNumber(n.number).includes(query);
                    }).map((n) => {
                      const isSelected = normalizeNumber(n.number)
                        === normalizeNumber(selectedNumber);
                      return (
                        <li
                          key={`number-${n.key}`}
                          className={isSelected ? styles.selected : ''}
                          onClick={() => setSelectedNumber(n.number)}
                        >
                          <p>{n.label}</p>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className={styles.toggleRow}>
                <div className={styles.toggle}>
                  <button
                    type="button"
                    className={mode === 'move' ? styles.active : ''}
                    onClick={() => setMode('move')}
                  >
                    Déplacer
                  </button>
                  <button
                    type="button"
                    className={mode === 'copy' ? styles.active : ''}
                    onClick={() => setMode('copy')}
                  >
                    Copier
                  </button>
                </div>
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  disabled={!selectedNumber}
                  onClick={requestTransfer}
                >
                  <span className="icon"><BsCheck size="24px" /></span>
                  <span> Valider </span>
                </button>
              </div>
            </>
          )}
        </div>
      </ModalStandalone>
    </>
  );
});

export default MoveImageToDocumentModal;
